import { createContext, useCallback, useContext, useEffect, useRef, useState, type ReactNode } from "react";

export type NotificationKind = "info" | "success" | "error" | "warning";

export type Notification = {
  message: string;
  kind: NotificationKind;
};

type NotificationContextValue = {
  show: (message: string, kind?: NotificationKind) => void;
};

const NotificationContext = createContext<NotificationContextValue | null>(null);

const TOAST_BASE_CLASSES =
  "fixed left-1/2 top-6 z-[1000] flex -translate-x-1/2 items-center gap-2 rounded-xl px-4 py-3 text-sm font-semibold text-white shadow-card transition-all duration-500 ease-in-out";

const TOAST_VARIANTS: Record<NotificationKind, string> = {
  info: "bg-semantic-info",
  success: "bg-semantic-success",
  error: "bg-semantic-error",
  warning: "bg-semantic-warning",
};

type NotificationStatus = "entering" | "active" | "exiting" | "idle";

export function NotificationProvider({ children }: { children: ReactNode }) {
  const [notification, setNotification] = useState<Notification | null>(null);
  const [status, setStatus] = useState<NotificationStatus>("idle");
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const exitTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const transitionRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      if (exitTimeoutRef.current) {
        clearTimeout(exitTimeoutRef.current);
      }
      if (transitionRef.current) {
        clearTimeout(transitionRef.current);
      }
    };
  }, []);

  const clear = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    if (exitTimeoutRef.current) {
      clearTimeout(exitTimeoutRef.current);
      exitTimeoutRef.current = null;
    }
    if (transitionRef.current) {
      clearTimeout(transitionRef.current);
      transitionRef.current = null;
    }
    setNotification(null);
    setStatus("idle");
  }, []);

  const show = useCallback<NotificationContextValue["show"]>(
    (message, kind = "info") => {
      clear();
      setNotification({ message, kind });
      setStatus("entering");

      transitionRef.current = setTimeout(() => {
        setStatus("active");
        transitionRef.current = null;
      }, 10);

      timeoutRef.current = setTimeout(() => {
        setStatus("exiting");
        exitTimeoutRef.current = setTimeout(() => {
          setNotification(null);
          setStatus("idle");
          exitTimeoutRef.current = null;
        }, 500);
        timeoutRef.current = null;
      }, 3000);
    },
    [clear],
  );

  const getStatusClasses = (status: NotificationStatus) => {
    switch (status) {
      case "entering":
        return "-translate-y-4 opacity-0";
      case "active":
        return "translate-y-0 opacity-100";
      case "exiting":
        return "translate-y-4 opacity-0";
      case "idle":
        return "hidden";
    }
  };

  return (
    <NotificationContext.Provider value={{ show }}>
      {children}
      {notification ? (
        <div
          className={`${TOAST_BASE_CLASSES} ${TOAST_VARIANTS[notification.kind]} ${getStatusClasses(status)}`}
          role="status"
        >
          {notification.message}
        </div>
      ) : null}
    </NotificationContext.Provider>
  );
}

export function useNotification() {
  const context = useContext(NotificationContext);
  if (context) {
    return context;
  }

  // Fallback no-op implementation for contexts rendered outside NotificationProvider
  return {
     
    show: () => {},
  };
}
